'use client';

import { memo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { nodeColors } from '@/lib/reactflowConfig';
import { EntityType } from '@/types/entities';
import { useGraphStore } from '@/stores/graphStore';

const TYPE_ICONS: Record<string, string> = {
  person: '\uD83D\uDC64',
  organization: '\uD83C\uDFE2',
  project: '\uD83D\uDCCB',
  location: '\uD83D\uDCCD',
  contract: '\uD83D\uDCC4',
  courtCase: '\u2696\uFE0F',
  auditFinding: '\uD83D\uDD0D',
  newsArticle: '\uD83D\uDCF0',
  document: '\uD83D\uDCC4',
};

function abbreviate(name: string): string {
  if (!name) return '?';
  const words = name.split(/\s+/).filter(Boolean);
  if (words.length === 1) return words[0].slice(0, 3).toUpperCase();
  return words
    .slice(0, 3)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
}

function EntityNodeInner({ data, selected, id }: NodeProps<any>) {
  const nodeType = (data.type || 'person') as EntityType;
  const color = nodeColors[nodeType] || nodeColors.person;
  const icon = TYPE_ICONS[nodeType] || '\u25CF';
  const abbr = abbreviate(data.label);
  const size = 56;
  const isPinned = useGraphStore((s) => s.pinnedNodes.has(id));

  return (
    <>
      <Handle
        type="target"
        position={Position.Top}
        style={{ opacity: 0, width: 1, height: 1, top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
      />
      <div
        title={`${data.label}\n${nodeType}`}
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          background: color.bg,
          border: `3px solid ${selected ? '#1a73e8' : color.border}`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: selected
            ? `0 0 0 3px rgba(26,115,232,0.3), 0 4px 12px rgba(0,0,0,0.2)`
            : '0 2px 8px rgba(0,0,0,0.15)',
          transition: 'box-shadow 0.2s, border-color 0.2s, transform 0.2s',
          userSelect: 'none',
          position: 'relative',
        }}
      >
        <span style={{ fontSize: 14, lineHeight: 1 }}>{icon}</span>
        <span
          style={{
            fontSize: 9,
            fontWeight: 700,
            color: color.text,
            lineHeight: 1.1,
            maxWidth: size - 10,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            textAlign: 'center',
          }}
        >
          {abbr}
        </span>
        {isPinned && (
          <span
            style={{
              position: 'absolute',
              top: -2,
              right: -2,
              fontSize: 10,
              lineHeight: 1,
            }}
            title="Pinned"
          >
            📌
          </span>
        )}
      </div>
      {/* Floating label below the circle */}
      <div
        style={{
          position: 'absolute',
          top: size + 4,
          left: '50%',
          transform: 'translateX(-50%)',
          fontSize: 10,
          fontWeight: 600,
          color: color.text,
          whiteSpace: 'nowrap',
          maxWidth: 120,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          textAlign: 'center',
          pointerEvents: 'none',
        }}
      >
        {data.label}
      </div>
      <Handle
        type="source"
        position={Position.Bottom}
        style={{ opacity: 0, width: 1, height: 1, top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
      />
    </>
  );
}

export const EntityNode = memo(EntityNodeInner);
